import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import {
  RemoteDiagnosticsSuccessResponseData,
  RemoteDiagnosticsVddGenericStructure,
} from '@vi-iot/client';
import { AppState } from '../app.state';

import { RawVddDetails } from './vdd-details-list.model';
import { VddDetailsListState } from './vdd-details-list.state';
import { matchVdd } from './vdd-details-list.reducer';
import { VddDetailsListHelper } from './vdd-details-list.helper';

export namespace VddDetailsListSelectors {
  export const VddDetailsList = (state: AppState): VddDetailsListState => state.vddDetailsList;

  export const getRawVddList = (): MemoizedSelector<
    AppState,
    RawVddDetails[],
    DefaultProjectorFn<RawVddDetails[]>
  > => createSelector(VddDetailsList, (state: VddDetailsListState) => state.vddDetailsList);

  export const getVddDetail = (
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<AppState, RawVddDetails, DefaultProjectorFn<RawVddDetails>> =>
    createSelector(VddDetailsList, (state: VddDetailsListState) =>
      state.vddDetailsList.find((vddDetailsItem) => matchVdd(vddDetailsItem, vddName, logicalLink))
    );

  export const getVddRawSingleReadValues = (
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsSuccessResponseData,
    DefaultProjectorFn<RemoteDiagnosticsSuccessResponseData>
  > =>
    createSelector(VddDetailsList, (state: VddDetailsListState) => {
      const vddDetails = state.vddDetailsList.find((vddDetailsItem) =>
        matchVdd(vddDetailsItem, vddName, logicalLink)
      );

      return vddDetails?.singleRead;
    });

  export const getVddpayloadForVddIsReadFalse = (
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsVddGenericStructure,
    DefaultProjectorFn<RemoteDiagnosticsVddGenericStructure>
  > =>
    createSelector(VddDetailsList, (state: VddDetailsListState) => {
      const vddDetails = state.vddDetailsList.find((vddDetailsItem) =>
        matchVdd(vddDetailsItem, vddName, logicalLink)
      );

      return vddDetails?.payloadForVddIsReadFalse;
    });

  export const getVddFlattenedMetadata = (
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsVddGenericStructure[],
    DefaultProjectorFn<RemoteDiagnosticsVddGenericStructure[]>
  > =>
    createSelector(VddDetailsList, (state: VddDetailsListState) => {
      const vddDetails = state.vddDetailsList.find((vddDetailsItem) =>
        matchVdd(vddDetailsItem, vddName, logicalLink)
      );

      return vddDetails?.flattenedStructure;
    });

  export const getMatchedValues = (
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<
    AppState,
    {
      matchedValues: RemoteDiagnosticsVddGenericStructure[];
      rawSingleRead: RemoteDiagnosticsSuccessResponseData;
      flattenedStructure: RemoteDiagnosticsVddGenericStructure[];
      flattenedMetadata: RemoteDiagnosticsVddGenericStructure[];
    },
    DefaultProjectorFn<{
      matchedValues: RemoteDiagnosticsVddGenericStructure[];
      rawSingleRead: RemoteDiagnosticsSuccessResponseData;
      flattenedStructure: RemoteDiagnosticsVddGenericStructure[];
      flattenedMetadata: RemoteDiagnosticsVddGenericStructure[];
    }>
  > =>
    createSelector(VddDetailsList, (state: VddDetailsListState) => {
      const vddDetails = state.vddDetailsList.find((vddDetailsItem) =>
        matchVdd(vddDetailsItem, vddName, logicalLink)
      );
      if (!!vddDetails?.flattenedStructure && !!vddDetails?.flattenedSingleRead) {
        return {
          matchedValues: VddDetailsListHelper.getMatchValues(
            vddDetails.flattenedStructure,
            vddDetails.flattenedSingleRead
          ),
          rawSingleRead: vddDetails.singleRead,
          flattenedStructure: vddDetails.flattenedStructure,
          flattenedMetadata: vddDetails.flattenedMetadata,
        };
      } else if (!!vddDetails?.flattenedStructure && !vddDetails?.flattenedSingleRead) {
        return {
          matchedValues: vddDetails.flattenedStructure,
          flattenedMetadata: vddDetails.flattenedMetadata,
          rawSingleRead: vddDetails.singleRead,
          flattenedStructure: vddDetails.flattenedStructure,
        };
      }

      return {
        matchedValues: [],
        rawSingleRead: null,
        flattenedStructure: [],
        flattenedMetadata: [],
      };
    });

  export const canDisplayVdd = (
    vddName: string,
    logicalLink: string,
    isReadVdd: boolean
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> =>
    createSelector(VddDetailsList, (state: VddDetailsListState) => {
      const vdd = state.vddDetailsList.find((vddDetailsItem) =>
        matchVdd(vddDetailsItem, vddName, logicalLink)
      );
      if (isReadVdd) {
        return !!(vdd?.structure && vdd?.singleRead);
      } else return !!vdd?.structure;
    });

  export const isVddLoading = (
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> =>
    createSelector(VddDetailsList, (state: VddDetailsListState) => {
      const vdd = state.vddDetailsList.find((vddDetailsItem) =>
        matchVdd(vddDetailsItem, vddName, logicalLink)
      );

      return vdd?.metadataLoading || vdd?.structureLoading || vdd?.singleReadLoading;
    });

  export const isVddReadInErrorState = (
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> =>
    createSelector(VddDetailsList, (state: VddDetailsListState) => {
      const vdd = state.vddDetailsList.find((vddDetailsItem) =>
        matchVdd(vddDetailsItem, vddName, logicalLink)
      );

      return (
        (!vdd?.metadataLoading && vdd?.metadataError) ||
        (!vdd?.structureLoading && vdd?.structureError) ||
        (!vdd?.singleReadLoading && vdd?.singleReadError)
      );
    });

  export const isAnyVddLoading = (): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > =>
    createSelector(VddDetailsList, (state: VddDetailsListState) =>
      state.vddDetailsList.some((vdd) => {
        return vdd?.metadataLoading || vdd?.structureLoading || vdd?.singleReadLoading;
      })
    );

  export const isVddWritingLoading = (
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> =>
    createSelector(VddDetailsList, (state: VddDetailsListState) => {
      if (state?.writingLoading) {
        return matchVdd(state.writingLoading, vddName, logicalLink)
          ? state.writingLoading.loading
          : false;
      } else {
        return false;
      }
    });

  export const isAnyVddWriting = (): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > => createSelector(VddDetailsList, (state: VddDetailsListState) => !!state.writingLoading);

  export const selectMatchedValues = (
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsVddGenericStructure[],
    DefaultProjectorFn<RemoteDiagnosticsVddGenericStructure[]>
  > =>
    createSelector(VddDetailsList, (state: VddDetailsListState) => {
      const vdd = state.vddDetailsList.find((vddDetailsItem) =>
        matchVdd(vddDetailsItem, vddName, logicalLink)
      );

      return vdd
        ? VddDetailsListHelper.getMatchValues(
            VddDetailsListHelper.getFlattenedStructure(vdd?.structure),
            VddDetailsListHelper.getFlattenedSingleRead(vdd?.singleRead)
          )
        : [];
    });
}
